import { FC } from "react";
import styled from "@emotion/styled";
import { MDSIcon } from "@/mds/components/icon/icon";
import { MDSIconColor, MDSIconKind } from "@/mds/components/icon/icon/icon.types";
import { MDSText } from "@/mds/components/text/text";
import { MDSTextColor, MDSTextVariant, MDSTextWeight } from "@/mds/components/text/text/text.types";

/**
 * Jump-to-latest affordance floating over a list scrolled away from its
 * bottom. Reports the unread count when there is one, so a reader catching up
 * knows how much is still below them.
 */
export const SessionListJumpToLatest: FC<{
  unreadCount?: number;
  onPress: () => void;
}> = ({ unreadCount = 0, onPress }) => {
  const hasUnread = unreadCount > 0;
  const label = hasUnread
    ? `${unreadCount} new ${unreadCount === 1 ? "message" : "messages"}`
    : "Jump to latest";

  return (
    <StyledJumpToLatest
      type="button"
      onClick={onPress}
      aria-label={label}
      $isEmphasized={hasUnread}
    >
      <MDSText
        variant={MDSTextVariant.TextXs}
        weight={hasUnread ? MDSTextWeight.SemiBold : MDSTextWeight.Medium}
        color={hasUnread ? MDSTextColor.OnAccent : MDSTextColor.Secondary}
      >
        {label}
      </MDSText>
      {/* Trailing, like every other destination affordance in MDS: the chevron
          says where pressing takes you, it does not label the control. */}
      <MDSIcon
        kind={MDSIconKind.AngleDown}
        size={12}
        boxSize={12}
        color={hasUnread ? MDSIconColor.OnAccent : MDSIconColor.Secondary}
      />
    </StyledJumpToLatest>
  );
};

const StyledJumpToLatest = styled.button<{ $isEmphasized: boolean }>(
  ({ theme, $isEmphasized }) => ({
    position: "absolute",
    // Clears the composer fade below the list rather than sitting on top of it.
    bottom: theme.spacing["6"],
    left: "50%",
    transform: "translateX(-50%)",
    zIndex: 2,
    display: "flex",
    alignItems: "center",
    gap: theme.spacing["1"],
    minHeight: 28,
    padding: `${theme.spacing["1"]} ${theme.spacing["3"]}`,
    borderRadius: 999,
    cursor: "pointer",
    whiteSpace: "nowrap",
    // With unread messages the pill is news the reader wants, so it takes the
    // accent. Without, it is a quiet escape hatch that reads as chrome rather
    // than competing with the conversation.
    border: `1px solid ${
      $isEmphasized ? theme.colors.background.button.emphasis.default : theme.colors.border.default
    }`,
    background: $isEmphasized
      ? theme.colors.background.button.emphasis.default
      : theme.colors.background.card.default,
    boxShadow: $isEmphasized ? "0 4px 16px rgb(0 0 0 / 20%)" : "0 2px 8px rgb(0 0 0 / 10%)",
  })
);
